import React, { useEffect, useState } from 'react'
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography, makeStyles
} from '@material-ui/core'
import PrintIcon from '@material-ui/icons/Print'
import api from '../../api/client'
import { thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import { printRequestReport } from './printRequestReport'

const useStyles = makeStyles(() => ({
  head: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))', gap: 10,
    marginBottom: 14,
  },
  box: {
    background: '#f7f8fc', border: '1px solid #e3e6f2', borderRadius: 10, padding: '10px 12px',
    borderRightWidth: 5,
  },
  boxTitle: { fontWeight: 800, fontSize: 12.5, marginBottom: 6 },
  line: { fontSize: 12, color: '#43476a', lineHeight: 2 },
  tableWrap: { overflowX: 'auto', border: '1px solid #e3e6f2', borderRadius: 10 },
  headCell: {
    background: '#1a237e', color: '#fff', fontWeight: 700, fontSize: 11.5,
    whiteSpace: 'nowrap', padding: '8px 10px',
  },
  cell: { fontSize: 12, whiteSpace: 'nowrap', padding: '8px 10px' },
  rowLabel: { fontWeight: 700, color: '#1a237e', background: '#f7f8fc', fontSize: 12 },
}))

// سه رنگِ متمایز برای سه بخش (پیمانکار / قرارداد / صورت‌وضعیت)
const SECTION_COLORS = {
  contractor: { bg: '#e8eaf6', border: '#1a237e', fg: '#1a237e' },
  contract: { bg: '#e8f5e9', border: '#015738', fg: '#015738' },
  request: { bg: '#fff3e0', border: '#b03a0a', fg: '#b03a0a' },
}

/**
 * گزارشِ یک صورت‌وضعیت:
 *   بالا ⇒ مشخصاتِ پیمانکار | مشخصاتِ قرارداد | مشخصاتِ صورت‌وضعیت
 *   پایین ⇒ سه سطر: تجمعی تا اینجا | تجمعیِ قبل از این صورت‌وضعیت | همین صورت‌وضعیت
 */
export default function RequestReportDialog({ open, onClose, contractId, requestId }) {
  const classes = useStyles()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!open || !contractId || !requestId) return undefined
    let alive = true
    setLoading(true)
    setErr('')
    api.get(`contracts/${contractId}/requests/${requestId}/report/`)
      .then((res) => { if (alive) setData(res.data) })
      .catch((e) => {
        if (alive) {
          setErr((e && e.response && e.response.data && e.response.data.detail) || 'خطا در دریافت گزارش')
        }
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [open, contractId, requestId])

  const ctr = data?.contractor || {}
  const cw = data?.contract || {}
  const rq = data?.request || {}

  return (
    <Dialog open={!!open} onClose={onClose} maxWidth="xl" fullWidth style={{ direction: 'rtl' }}>
      <DialogTitle style={{ fontWeight: 800, color: '#1a237e' }}>
        گزارشِ صورت‌وضعیت
      </DialogTitle>
      <DialogContent dividers>
        {loading && <Box textAlign="center" style={{ padding: 20 }}><CircularProgress /></Box>}
        {err && <Typography color="error">{err}</Typography>}

        {!loading && data && (
          <>
            <Box className={classes.head}>
              <Paper className={classes.box} variant="outlined"
                style={{ background: SECTION_COLORS.contractor.bg, borderRightColor: SECTION_COLORS.contractor.border }}>
                <Typography className={classes.boxTitle} style={{ color: SECTION_COLORS.contractor.fg }}>
                  مشخصات پیمانکار
                </Typography>
                <Typography className={classes.line}>نام: {toFarsiDigits(ctr.name || '—')}</Typography>
                <Typography className={classes.line}>شناسه ملی: {toFarsiDigits(ctr.national_code || '—')}</Typography>
                <Typography className={classes.line}>کد اقتصادی: {toFarsiDigits(ctr.economic_code || '—')}</Typography>
              </Paper>

              <Paper className={classes.box} variant="outlined"
                style={{ background: SECTION_COLORS.contract.bg, borderRightColor: SECTION_COLORS.contract.border }}>
                <Typography className={classes.boxTitle} style={{ color: SECTION_COLORS.contract.fg }}>
                  مشخصات قرارداد
                </Typography>
                <Typography className={classes.line}>شماره: {toFarsiDigits(cw.number || '—')}</Typography>
                <Typography className={classes.line}>تاریخ: {toFarsiDigits(cw.date || '—')}</Typography>
                <Typography className={classes.line}>سازمان: {toFarsiDigits(cw.organization || '—')}</Typography>
                <Typography className={classes.line}>موضوع: {toFarsiDigits(cw.subject || 'نامشخص')}</Typography>
                <Typography className={classes.line}>
                  مبلغ اولیه: {toFarsiDigits(thousandsSeparate(cw.contract_cost || 0))}
                </Typography>
                <Typography className={classes.line}>
                  افزایش/کاهش: {toFarsiDigits(thousandsSeparate(cw.increase_decrease_cost || 0))}
                </Typography>
                <Typography className={classes.line}>
                  مبلغ قرارداد: {toFarsiDigits(thousandsSeparate(cw.contract_total || 0))}
                </Typography>
              </Paper>

              <Paper className={classes.box} variant="outlined"
                style={{ background: SECTION_COLORS.request.bg, borderRightColor: SECTION_COLORS.request.border }}>
                <Typography className={classes.boxTitle} style={{ color: SECTION_COLORS.request.fg }}>
                  مشخصات صورت‌وضعیت
                </Typography>
                <Typography className={classes.line}>شماره نامه: {toFarsiDigits(rq.letter_number || '—')}</Typography>
                <Typography className={classes.line}>تاریخ نامه: {toFarsiDigits(rq.letter_date || '—')}</Typography>
                <Typography className={classes.line}>نوع صورت‌وضعیت: {toFarsiDigits(rq.type || '—')}</Typography>
                <Typography className={classes.line}>شماره: {toFarsiDigits(rq.number || '—')}</Typography>
                <Typography className={classes.line}>از تاریخ: {toFarsiDigits(rq.from_date || '—')}</Typography>
                <Typography className={classes.line}>تا تاریخ: {toFarsiDigits(rq.to_date || '—')}</Typography>
              </Paper>
            </Box>

            <Box className={classes.tableWrap}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} align="right">شرح</TableCell>
                    {(data.columns || []).map((c) => (
                      <TableCell key={c.key} className={classes.headCell} align="center">{c.label}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data.rows || []).map((row) => (
                    <TableRow key={row.key}>
                      <TableCell className={classes.rowLabel} align="right">{row.label}</TableCell>
                      {(data.columns || []).map((c) => (
                        <TableCell key={c.key} className={classes.cell} align="center">
                          {toFarsiDigits(thousandsSeparate((row.values || {})[c.key] || 0))}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => printRequestReport(data)}
          color="default"
          variant="outlined"
          startIcon={<PrintIcon />}
          disabled={!data}
          style={{ fontWeight: 'bold', marginLeft: 'auto' }}
        >
          چاپ گزارش (PDF)
        </Button>
        <Button onClick={onClose} color="primary" variant="contained" style={{ fontWeight: 'bold' }}>
          بستن
        </Button>
      </DialogActions>
    </Dialog>
  )
}
